import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Paperclip, X } from 'lucide-react';
import { colors, spacing, typography, borderRadius } from '../../../styles/theme';
import {
  cancelJob,
  getJobProgress,
  getJobResults,
  startMatchJob,
  uploadFile,
  type JobProgress,
  type UploadPreview,
} from '../../../services/adminAgentApi';

/**
 * Upload, mapping preview and match-job progress.
 *
 * The preview is shown for correction before matching starts: inferred field
 * mapping and object types are a proposal, not a decision. Matching itself is
 * read-only, which the panel states so it is never mistaken for an import.
 */

const Panel = styled.div`
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]};
  margin: ${spacing[2]} 0;
  background: ${colors.background.secondary};
  font-size: ${typography.fontSizes.sm};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
`;

const Muted = styled.div`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  margin-top: ${spacing[1]};
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  border: 1px solid ${colors.border.normal};
  background: ${colors.white};
  color: ${colors.text.primary};
  border-radius: ${borderRadius.base};
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PrimaryButton = styled(Button)`
  border-color: ${colors.primary[600]};
  background: ${colors.primary[600]};
  color: ${colors.white};
`;

const Bar = styled.div`
  height: 6px;
  border-radius: ${borderRadius.base};
  background: ${colors.gray[200]};
  overflow: hidden;
  margin-top: ${spacing[2]};
`;

const Fill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${colors.primary[500]};
  transition: width 200ms ease;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${spacing[2]};
  font-size: ${typography.fontSizes.xs};

  th, td {
    text-align: left;
    padding: ${spacing[1]} ${spacing[2]};
    border-bottom: 1px solid ${colors.border.light};
  }
`;

const BAND_COLORS: Record<string, string> = {
  exact: colors.success[600],
  strong: colors.success[600],
  possible: colors.warning[600],
  none: colors.text.tertiary,
};

const Band = styled.span<{ $band: string }>`
  color: ${props => BAND_COLORS[props.$band] ?? colors.text.tertiary};
  font-weight: ${typography.fontWeights.semibold};
  white-space: nowrap;
`;

const Reasons = styled.div`
  color: ${colors.text.secondary};
  margin-top: 2px;
`;

const OBJECT_TYPES = ['event', 'eventSource', 'entity'] as const;
const POLL_MS = 3000;

/** Result values arrive untyped from the job report. */
function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

const FINISHED = ['completed', 'partially_completed', 'failed', 'canceled'];

/** One uploaded row beside the catalog record it was matched to. */
interface MatchResultRow {
  row: number;
  objectType?: string;
  normalized?: Record<string, unknown>;
  match?: {
    score: number;
    band: 'exact' | 'strong' | 'possible' | 'none';
    reasons?: string[];
    candidate?: Record<string, unknown> | null;
  } | null;
  suggestedAction?: string;
}

export function UploadPanel({
  conversationId,
  onJobUpdate,
}: {
  conversationId?: string;
  onJobUpdate?: (progress: JobProgress) => void;
}) {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [preview, setPreview] = useState<UploadPreview | null>(null);
  const [overrideType, setOverrideType] = useState<string>('');
  const [job, setJob] = useState<JobProgress | null>(null);
  const [results, setResults] = useState<MatchResultRow[] | null>(null);
  const [showAllResults, setShowAllResults] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reset = () => {
    setUploadId(null);
    setFilename(null);
    setPreview(null);
    setOverrideType('');
    setJob(null);
    setResults(null);
    setShowAllResults(false);
    setError(null);
  };

  const onSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await uploadFile(file, conversationId);
      setUploadId(result.uploadId);
      setFilename(file.name);
      setPreview(result.preview);
    } catch (uploadError) {
      setError((uploadError as Error).message);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onMatch = async () => {
    if (!uploadId) return;
    setBusy(true);
    try {
      const result = await startMatchJob(uploadId, {
        conversationId,
        objectType: overrideType || undefined,
      });
      setJob(result.job);
    } catch (matchError) {
      setError((matchError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const refreshJob = useCallback(async (jobId: string) => {
    try {
      const result = await getJobProgress(jobId);
      setJob(result.progress);
      onJobUpdate?.(result.progress);
      return result.progress;
    } catch {
      return null;
    }
  }, [onJobUpdate]);

  useEffect(() => {
    if (!job || FINISHED.includes(job.status)) {
      return undefined;
    }
    const timer = setInterval(() => refreshJob(job.jobId), POLL_MS);
    return () => clearInterval(timer);
  }, [job, refreshJob]);

  // The per-row report is what says which uploaded event matched what, so it is
  // loaded as soon as there is something to report rather than on request.
  useEffect(() => {
    if (!job || !['completed', 'partially_completed'].includes(job.status)) return;
    let cancelled = false;
    getJobResults(job.jobId)
      .then(result => { if (!cancelled) setResults(result.rows as unknown as MatchResultRow[]); })
      .catch(() => { if (!cancelled) setResults(null); });
    return () => { cancelled = true; };
  }, [job]);

  return (
    <Panel aria-label="Bulk upload">
      <Row>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.txt"
          onChange={onSelect}
          style={{ display: 'none' }}
          aria-label="Choose a .txt or .csv file"
        />
        <Button type="button" onClick={() => fileInput.current?.click()} disabled={busy}>
          <Paperclip size={14} aria-hidden /> {filename ? 'Choose another file' : 'Upload .txt or .csv'}
        </Button>
        {filename && (
          <Button type="button" onClick={reset} aria-label="Clear upload">
            <X size={14} aria-hidden /> Clear
          </Button>
        )}
      </Row>

      {error && (
        <Muted role="alert" style={{ color: colors.error[600] }}>{error}</Muted>
      )}

      {preview && (
        <>
          <Muted>
            {filename} · {preview.recordCount} record(s)
            {preview.truncated && ' (truncated at 1,000)'}
            {preview.isMixed && ' · mixed object types'}
          </Muted>

          <Muted>
            Detected:{' '}
            {Object.entries(preview.objectTypeCounts)
              .map(([type, count]) => `${count} ${type}`)
              .join(', ')}
          </Muted>

          {Object.keys(preview.mapping).length > 0 && (
            <Muted>
              Field mapping:{' '}
              {Object.entries(preview.mapping)
                .map(([field, header]) => `${field} ← ${header}`)
                .join(', ')}
            </Muted>
          )}

          {preview.unmappedHeaders.length > 0 && (
            <Muted>Unmapped columns: {preview.unmappedHeaders.join(', ')}</Muted>
          )}

          <Row style={{ marginTop: spacing[2] }}>
            <label htmlFor="object-type-override" style={{ fontSize: typography.fontSizes.xs }}>
              Treat every row as
            </label>
            <select
              id="object-type-override"
              value={overrideType}
              onChange={changeEvent => setOverrideType(changeEvent.target.value)}
            >
              <option value="">Detected type per row</option>
              {OBJECT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <PrimaryButton type="button" onClick={onMatch} disabled={busy || Boolean(job)}>
              Match records
            </PrimaryButton>
          </Row>

          <Muted>Matching is read-only. Nothing is created without a separate approval.</Muted>

          <Table>
            <thead>
              <tr>
                <th>Row</th>
                <th>Type</th>
                <th>Preview</th>
                <th>Missing</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.slice(0, 5).map(row => (
                <tr key={row.row}>
                  <td>{row.row}</td>
                  <td>{row.objectType}</td>
                  <td>{String(row.normalized.title ?? row.normalized.entityName ?? row.normalized.url ?? '')}</td>
                  <td>{row.missingRequiredFields.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {preview.rows.length > 5 && <Muted>Showing 5 of {preview.rows.length} rows.</Muted>}
        </>
      )}

      {job && (
        <div aria-live="polite">
          <Muted>
            Job {job.status} · {job.processedRows}/{job.totalRows} rows ·{' '}
            {job.matchedRows} matched · {job.unmatchedRows} unmatched
            {job.failedRows > 0 && ` · ${job.failedRows} failed`}
          </Muted>
          <Bar>
            <Fill $percent={job.percentComplete} />
          </Bar>
          {!['completed', 'partially_completed', 'failed', 'canceled'].includes(job.status) && (
            <Button
              type="button"
              style={{ marginTop: spacing[2] }}
              onClick={async () => setJob((await cancelJob(job.jobId)).progress)}
            >
              Cancel job
            </Button>
          )}
          {['completed', 'partially_completed'].includes(job.status) && (
            <Muted>
              Ask the agent to review job {job.jobId.slice(0, 8)} or to plan additions from it.
            </Muted>
          )}

          {results && results.length > 0 && (
            <>
              <Table aria-label="Match results">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Uploaded</th>
                    <th>Confidence</th>
                    <th>Best match in the catalog</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllResults ? results : results.slice(0, 10)).map(result => {
                    const provided = result.normalized ?? {};
                    const candidate = result.match?.candidate ?? null;
                    const band = result.match?.band ?? 'none';
                    return (
                      <tr key={result.row}>
                        <td>{result.row}</td>
                        <td>
                          {text(provided.title) || text(provided.entityName) || text(provided.url) || '—'}
                          <Reasons>
                            {[text(provided.startDate), text(provided.city)].filter(Boolean).join(' · ')}
                          </Reasons>
                        </td>
                        <td>
                          <Band $band={band}>
                            {band !== 'none'
                              ? `${band} ${result.match?.score ?? 0}`
                              : candidate ? `best guess ${result.match?.score ?? 0}` : 'No match'}
                          </Band>
                        </td>
                        <td>
                          {candidate
                            ? (
                              <>
                                {text(candidate.title) || text(candidate.entityName)
                                  || text(candidate.url) || `#${text(candidate.eventId)}`}
                                <Reasons>{(result.match?.reasons ?? []).join(' · ')}</Reasons>
                              </>
                            )
                            : <Muted as="span">Nothing similar found</Muted>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {results.length > 10 && (
                <Button type="button" onClick={() => setShowAllResults(current => !current)}>
                  {showAllResults
                    ? 'Show fewer'
                    : `Show all ${results.length} rows`}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

export default UploadPanel;
